import { useState, useEffect } from "react";

/**
 * Custom hook to manage carousel business logic
 * @param {Array<string>} images - Array of image URLs
 * @returns {Object} Carousel state and functions
 */
export const useCarousel = (images) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    // Stores numeric aspect ratios per image index (width / height)
    const [imageAspectRatios, setImageAspectRatios] = useState({});
    const [loadedImages, setLoadedImages] = useState(new Set());

    const totalSlides = images.length;

    // Reset loaded images when images array changes
    useEffect(() => {
        setLoadedImages(new Set());
        setImageAspectRatios({});
        setCurrentSlide(0);
    }, [images.length]);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    const goToSlide = (index) => {
        if (index >= 0 && index < totalSlides) {
            setCurrentSlide(index);
        }
    };

    const handleImageLoad = (index, event) => {
        const img = event.target;
        const aspectRatio = img.naturalWidth / img.naturalHeight;

        // Store the precise aspect ratio for this image
        setImageAspectRatios(prev => ({
            ...prev,
            [index]: aspectRatio
        }));

        // Track loaded images for progress
        setLoadedImages(prev => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
        });
    };

    const showLeftArrow = currentSlide > 0;
    const showRightArrow = currentSlide < totalSlides - 1;

    // Decide object-fit based on how close the image is to 4:3
    const getImageObjectFit = (index) => {
        const ratio = imageAspectRatios[index];
        if (!ratio) return 'object-cover';

        const is4to3 = Math.abs(ratio - 4 / 3) < 0.1; // Allow small tolerance for floating point comparison
        return is4to3 ? 'object-contain' : 'object-cover';
    };

    // Container should follow the current slide's aspect ratio when known,
    // otherwise fall back to a default 4:3.
    const getContainerAspectRatio = () => {
        const currentRatio = imageAspectRatios[currentSlide];
        return  4 / 3;
    };

    const isImageLoaded = (index) => loadedImages.has(index);

    return {
        currentSlide,
        totalSlides,
        imageAspectRatios,
        showLeftArrow,
        showRightArrow,
        nextSlide,
        prevSlide,
        goToSlide,
        handleImageLoad,
        getImageObjectFit,
        getContainerAspectRatio,
        isImageLoaded,
    };
};

